/**
 * F-132: CompletenessWidget.
 * Progress bar + clickable checklist that deep-links to each editor.
 * Backend already computes `completeness_pct`; this widget surfaces it
 * + decomposes it into actionable checkboxes.
 */
import type { IconName } from '@/components/atoms';
import { Card, Icon } from '@/components/atoms';
import { Link } from 'react-router-dom';
import type { SpecialistProfile, User } from '@/types/domain';
import styles from './CompletenessWidget.module.css';

interface ChecklistItem {
  key: string;
  label: string;
  weight: number;
  done: boolean;
  to: string;
  icon: IconName;
}

export interface CompletenessWidgetProps {
  user: User;
  profile: SpecialistProfile;
  bookingHoursDone?: boolean;
  ordersCount?: number;
}

export function CompletenessWidget({
  user,
  profile,
  bookingHoursDone = false,
  ordersCount = 0,
}: CompletenessWidgetProps): JSX.Element {
  // F-180 (B6): added `title` + `service_area` rows. Weights now mirror
  // the backend formula in `apps/profiles/services/completeness.py`
  // (sum == 100). Keep the two in sync — if you change a weight here,
  // change it there.
  const items: ChecklistItem[] = [
    {
      key: 'avatar',
      label: 'Аватар',
      weight: 4,
      done: Boolean(profile.avatar_url),
      to: '/me/edit',
      icon: 'me',
    },
    {
      key: 'bio',
      label: 'Біо',
      weight: 6,
      done: Boolean(profile.bio && profile.bio.length >= 40),
      to: '/me/edit',
      icon: 'edit',
    },
    {
      key: 'title',
      label: 'Заголовок (хто ти за фахом)',
      weight: 4,
      done: Boolean(profile.title && profile.title.trim().length > 0),
      to: '/me/edit',
      icon: 'edit',
    },
    {
      key: 'service_area',
      label: 'Зона обслуговування на карті',
      weight: 4,
      done: (profile.service_areas ?? []).length > 0,
      to: '/me/service-area',
      icon: 'map-pin',
    },
    {
      key: 'cat',
      label: 'Принаймні 1 послуга',
      weight: 8,
      done: (profile.categories ?? []).length > 0,
      to: '/me/categories',
      icon: 'search',
    },
    {
      key: 'city',
      label: 'Принаймні 1 місто',
      weight: 4,
      done: profile.city != null,
      to: '/me/cities',
      icon: 'home',
    },
    {
      key: 'presets',
      label: 'Принаймні 1 послуга-preset',
      weight: 10,
      done: (profile.presets ?? []).length > 0,
      to: '/me/presets',
      icon: 'presets',
    },
    {
      key: 'portfolio',
      label: 'Принаймні 3 портфоліо роботи',
      weight: 14,
      done: (profile.portfolio ?? []).length >= 3,
      to: '/me/portfolio',
      icon: 'image',
    },
    {
      key: 'qa',
      label: 'Принаймні 1 Q&A',
      weight: 6,
      done: (profile.qna ?? []).length > 0,
      to: '/me/qa',
      icon: 'info',
    },
    {
      key: 'verified',
      label: 'Верифікація пройдена',
      weight: 18,
      done: profile.is_verified || user.is_specialist_verified === true,
      to: '/me/verification',
      icon: 'verify',
    },
    {
      key: 'hours',
      label: 'Робочі години задано',
      weight: 6,
      done: bookingHoursDone,
      to: '/me/working-hours',
      icon: 'calendar',
    },
    {
      key: 'orders',
      label: 'Принаймні 1 успішне замовлення',
      weight: 16,
      done: ordersCount > 0,
      to: '/orders',
      icon: 'orders',
    },
  ];

  const pct =
    typeof (profile as SpecialistProfile & { completeness_pct?: number }).completeness_pct ===
    'number'
      ? (profile as SpecialistProfile & { completeness_pct?: number }).completeness_pct ?? 0
      : items.filter((i) => i.done).reduce((acc, i) => acc + i.weight, 0);

  return (
    <Card>
      <strong>Заповненість профілю</strong>
      <div className={styles.bar} aria-label={`Заповнено ${pct}%`}>
        <div className={styles.fill} style={{ width: `${pct}%` }} />
      </div>
      <div className={styles.pct}>{pct}%</div>
      {/* F-180 D9: unchecked rows are real <Link> elements (not onClick
          on an <li>) — gives us right-click "open in new tab", correct
          tab/focus order, and SPA navigation prefetching for free.
          Done rows stay plain <li>. */}
      <ul className={styles.list}>
        {items.map((item) =>
          item.done ? (
            <li key={item.key} className={styles.done}>
              <Icon name={item.icon} size={16} />
              <span style={{ flex: 1 }}>{item.label}</span>
              <span className={styles.weight}>+{item.weight}%</span>
            </li>
          ) : (
            <li key={item.key} className={styles.todo}>
              <Link
                to={item.to}
                className={styles.todoLink}
                aria-label={`${item.label} — заповнити`}
              >
                <Icon name={item.icon} size={16} />
                <span style={{ flex: 1 }}>{item.label}</span>
                <span className={styles.weight}>+{item.weight}%</span>
              </Link>
            </li>
          ),
        )}
      </ul>
    </Card>
  );
}
